import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Stack,
  useMediaQuery,
  Switch,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { getAllCarriers } from "../../redux/action/admin";
import { changeActivationStatus } from "../../redux/action/carrier";

const AdminCarriers = () => {
  const dispatch = useDispatch();
  const [carriers, setCarriers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedCarrier, setSelectedCarrier] = useState<any | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [openPaymentsDialog, setOpenPaymentsDialog] = useState(false);
  const [openSubscriptionsDialog, setOpenSubscriptionsDialog] = useState(false);
  const [totalCarriers, setTotalCarriers] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const fetchCarriers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await dispatch<any>(
        getAllCarriers({
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
        })
      );
      const res = unwrapResult(result);
      setCarriers(res.data);
      setTotalCarriers(res.total);
    } catch (err: any) {
      setError(err?.message || "Failed to load carriers");
      console.error("Error fetching carriers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCarriers();
  }, [page, rowsPerPage, searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleViewPayments = async (carrier: any) => {
    setPaymentsLoading(true);
    setPaymentsError(null);
    setSelectedCarrier(carrier);

    try {
      setPayments(carrier.subscription_payments || []);
      setOpenPaymentsDialog(true);
    } catch (err: any) {
      setPaymentsError(err?.message || "Failed to load payments");
      console.error("Error fetching payments:", err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleClosePaymentsDialog = () => {
    setOpenPaymentsDialog(false);
    setPayments([]);
    setSelectedCarrier(null);
  };

  const handleViewDetails = (carrier: any) => {
    setSelectedCarrier(carrier);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCarrier(null);
  };

  const handleViewSubscriptions = (carrier: any) => {
    setSelectedCarrier(carrier);
    setOpenSubscriptionsDialog(true);
  };

  const handleCloseSubscriptionsDialog = () => {
    setOpenSubscriptionsDialog(false);
    setSelectedCarrier(null);
  };
  const handleToggleChange = (carrierId: string, checked: boolean) => {
    dispatch<any>(changeActivationStatus(carrierId))
      .then(unwrapResult)
      .then((res: any) => {
        setCarriers(
          carriers.map((c) =>
            c.canonical_id === carrierId
              ? { ...c, isActivated: !c.isActivated }
              : c
          )
        );
      });
  };
  return (
    <Box>
      <Paper
        sx={{
          p: isMobile ? 1 : 2,
          mb: isMobile ? 1 : 2,
          width: "100%",
          overflowX: "auto",
          maxWidth: "calc(100vw - 32px)",
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: isMobile ? 1 : 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}
          >
            Carrier Management
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchCarriers}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search carriers..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ color: "action.active", mr: 1 }} />
            ),
          }}
          sx={{ mb: 2, maxWidth: "100%" }}
        />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "700" }}>
                      Permanent Activation
                    </TableCell>
                    <TableCell sx={{ fontWeight: "700" }}>
                      Business Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "700" }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: "700" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "700" }}>
                      Payment Made
                    </TableCell>
                    <TableCell sx={{ fontWeight: "700" }}>Created At</TableCell>

                    <TableCell sx={{ fontWeight: "700", textAlign: "center" }}>
                      Payments
                    </TableCell>
                    <TableCell sx={{ fontWeight: "700", textAlign: "center" }}>
                      Subscriptions
                    </TableCell>
                    <TableCell sx={{ fontWeight: "700", textAlign: "center" }}>
                      Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carriers.map((carrier: any) => (
                    <TableRow key={carrier.id}>
                      <TableCell>
                        <Switch
                          checked={carrier.isActivated}
                          onChange={(e) =>
                            handleToggleChange(
                              carrier.canonical_id,
                              e.target.checked
                            )
                          }
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{carrier.business_name}</TableCell>

                      <TableCell>{carrier.business_name}</TableCell>
                      <TableCell>{carrier.email}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            p: "4px 8px",
                            borderRadius: 1,
                            backgroundColor: carrier.isActivated
                              ? "success.light"
                              : "error.light",
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        >
                          {carrier.isActivated ? "Active" : "Inactive"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {carrier.isActivated ? "YES" : "NO"}
                      </TableCell>
                      <TableCell>
                        {new Date(carrier.created_at).toLocaleDateString()}
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewPayments(carrier)}
                        >
                          View Payments
                        </Button>
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewSubscriptions(carrier)}
                        >
                          View Subscriptions
                        </Button>
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewDetails(carrier)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 15]}
              component="div"
              count={totalCarriers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Carrier Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Carrier Details</DialogTitle>
        <DialogContent sx={{ padding: isMobile ? 1 : 3 }}>
          {selectedCarrier && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Business Name:</strong> {selectedCarrier.business_name}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Email:</strong> {selectedCarrier.email}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong>{" "}
                {selectedCarrier.isActivated ? "Active" : "Inactive"}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Created:</strong>{" "}
                {new Date(selectedCarrier.created_at).toLocaleString()}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Phone:</strong> {selectedCarrier.business_phone}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Stripe Onboarded:</strong>{" "}
                {selectedCarrier.stripe_onboarded ? "Yes" : "No"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {/* <Button
            variant="contained"
            onClick={() => {
              console.log("Edit", selectedCarrier?.id);
              handleCloseDialog();
            }}
          >
            Edit Carrier
          </Button> */}
        </DialogActions>
      </Dialog>

      {/* Payments Dialog */}
      <Dialog
        open={openPaymentsDialog}
        onClose={handleClosePaymentsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            animation: openPaymentsDialog
              ? "fadeScaleIn 0.3s ease forwards"
              : "fadeScaleOut 0.3s ease forwards",
          },
        }}
      >
        <style>
          {`
            @keyframes fadeScaleIn {
              0% {
                opacity: 0;
                transform: scale(0.95);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes fadeScaleOut {
              0% {
                opacity: 1;
                transform: scale(1);
              }
              100% {
                opacity: 0;
                transform: scale(0.95);
              }
            }
            @keyframes spin {
              0% { transform: rotate(0deg);}
              100% { transform: rotate(360deg);}
            }
          `}
        </style>

        <DialogTitle>
          Payments for {selectedCarrier?.business_name || "Carrier"}
        </DialogTitle>

        <DialogContent>
          {paymentsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 4,
              }}
            >
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main,
                  animation: "spin 1s linear infinite",
                }}
              />
            </Box>
          ) : paymentsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {paymentsError}
            </Alert>
          ) : payments.length === 0 ? (
            <Typography variant="body1" sx={{ p: 2 }}>
              No payments found for this carrier.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Type of Subscription</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow
                      key={payment.id}
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 0.3s ease",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell>
                        ${(payment.amount_in_cents / 100).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {payment.checkout_time
                          ? new Date(payment.checkout_time).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {payment.is_sms_tool_included === true &&
                        payment.is_booking_tool_included === false
                          ? "SMS Tool"
                          : payment.is_sms_tool_included === false &&
                            payment.is_booking_tool_included === true
                          ? "Booking Tool"
                          : payment.is_sms_tool_included === true &&
                            payment.is_booking_tool_included === true
                          ? "SMS Tool + Booking Tool"
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClosePaymentsDialog}
            variant="contained"
            color="primary"
            sx={{
              marginLeft: "auto",
              fontWeight: "bold",
              transition: "transform 0.2s ease, background-color 0.3s ease",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
                transform: "scale(1.05)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscriptions Dialog */}
      <Dialog
        open={openSubscriptionsDialog}
        onClose={handleCloseSubscriptionsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Active Subscriptions for{" "}
          <strong>{selectedCarrier?.business_name || "Carrier"}</strong>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCarrier?.subscriptions &&
          selectedCarrier.subscriptions.length > 0 ? (
            <TableContainer>
              <Table size="small" aria-label="subscriptions table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Subscription Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedCarrier.subscriptions.map((sub: any) => (
                    <TableRow key={sub.id}>
                      <TableCell>
                        {sub.subscription_type === "bookingTool" ||
                        sub.subscription_type === "bookingTool Upgrade"
                          ? "Booking Tool"
                          : sub.subscription_type === "smsTool" ||
                            sub.subscription_type === "smsTool Upgrade"
                          ? "SMS Tool"
                          : "Booking Tool + SMS Tool (Combo)"}
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            p: "4px 8px",
                            borderRadius: 1,
                            backgroundColor:
                              sub.status === "active"
                                ? "success.light"
                                : "error.light",
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        >
                          {sub.status.toUpperCase()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {sub.created_at
                          ? new Date(sub.created_at).toLocaleDateString()
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No active subscriptions.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSubscriptionsDialog}
            variant="contained"
            color="primary"
            sx={{ fontWeight: "bold" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCarriers;
